import { API_ENDPOINTS, apiService } from "../api";
import type {
  SlackChannelConnectionRead,
  SlackChannelsListResponse,
  SlackSendMessageResponse,
} from "./slack.types";

/**
 * Real Slack integration endpoints. Distinct from the temporary
 * `stories.slackDebugSendMessage` debug endpoint, which this replaces
 * once the connect UI is wired up end to end.
 */

/** Resolves to the connection row, or `null` if Slack isn't connected yet
 * (backend returns 200 with a null body when there's no connection row for this tenant/channel). */
export async function getSlackConnectionStatus(): Promise<SlackChannelConnectionRead | null> {
  try {
    const connection = await apiService.get<SlackChannelConnectionRead | null>(
      API_ENDPOINTS.notifications.channelConnection("slack"),
    );
    return connection ?? null;
  } catch {
    // apiService's interceptor collapses the original HTTP status into a
    // generic Error, so a genuine failure and "not connected" (null body) aren't
    // distinguishable here. Treating any failure as "not connected" is the
    // safe default for this read — worst case the user sees the connect
    // button and retries.
    return null;
  }
}

/** Fetch the {authorize_url} for a popup-mode Slack connect attempt.
 * `returnTo` should be a same-origin relative path (e.g. the current route)
 * so the backend can round-trip it — see slack_oauth_service.py's
 * `return_to` state claim for the dynamic-redirect requirement. */
export async function fetchSlackAuthorizeUrl(returnTo?: string): Promise<string> {
  const { authorize_url } = await apiService.get<{ authorize_url: string }>(
    API_ENDPOINTS.notifications.slackOauthAuthorize({ returnTo, mode: "popup" }),
  );
  return authorize_url;
}

/** Full-page fallback when the popup is blocked by the browser. */
export function beginSlackOAuthRedirect(returnTo?: string): void {
  window.location.assign(
    API_ENDPOINTS.notifications.slackOauthAuthorize({ returnTo, mode: "redirect" }),
  );
}

/** Disconnects the tenant's current Slack workspace — same generic
 * DELETE .../channels/{channel}/connection endpoint used for any channel
 * (Slack, Teams, email, ...), not Slack-specific. */
export async function disconnectSlack(): Promise<void> {
  await apiService.delete(API_ENDPOINTS.notifications.channelConnection("slack"));
}

export async function listSlackChannels(): Promise<SlackChannelsListResponse> {
  return apiService.get<SlackChannelsListResponse>(
    API_ENDPOINTS.notifications.slackChannels(),
  );
}

/** Alternative to passing `file` directly — the backend renders the deck
 * itself (story_presentation_service.render_deck) instead of requiring an
 * already-exported file, so the caller never needs a browser-side export
 * step (see storyExportApi.ts::sendStoryToSlack). */
export interface SlackStoryAttachmentRef {
  storyId: string;
  workspaceId: string;
  format?: "pdf" | "pptx";
  /** Presentation theme to render in; omitted → the default theme. */
  theme?: string;
  /** A deck already rendered for this story/format (the Deliver story
   *  preview) — attached as-is instead of rendering again. */
  artifactId?: string;
}

export async function sendSlackMessage(
  channelId: string,
  text: string,
  file?: File,
  storyRef?: SlackStoryAttachmentRef,
): Promise<SlackSendMessageResponse> {
  const formData = new FormData();
  formData.append("channel_id", channelId);
  formData.append("text", text);
  if (file) {
    formData.append("file", file);
  } else if (storyRef) {
    formData.append("story_id", storyRef.storyId);
    formData.append("workspace_id", storyRef.workspaceId);
    formData.append("format", storyRef.format ?? "pdf");
    if (storyRef.theme) formData.append("theme", storyRef.theme);
    if (storyRef.artifactId) formData.append("artifact_id", storyRef.artifactId);
  }

  return apiService.postFormData<SlackSendMessageResponse>(
    API_ENDPOINTS.notifications.slackSendMessage(),
    formData,
  );
}
